// src/subscreens/superadmin/earnings/EarningsPage.jsx

import React, { useState, useMemo, useEffect, useContext } from 'react';
import {
  FiCreditCard, FiTrendingUp, FiPercent, FiDollarSign
} from 'react-icons/fi';
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import MasterLayout from '../../../layout/superadmin/MasterLayout.js';
import AppContext from '../../../context/AppContext.js';
import '../../../assets/css/superadmin/earningmanagment/EarningManagment.css';

// ─────────────────────────────────────────────
// FORMAT HELPERS
// ─────────────────────────────────────────────
const formatAxisTick = (value) => {
  if (value >= 1000000) return `Rs ${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000)    return `Rs ${(value / 1000).toFixed(0)}K`;
  return `Rs ${value}`;
};

const formatTooltipValue = (value) => {
  if (value >= 1000000) return [`Rs ${(value / 1000000).toFixed(2)}M`, 'Revenue'];
  if (value >= 1000)    return [`Rs ${(value / 1000).toFixed(1)}K`, 'Revenue'];
  return [`Rs ${value}`, 'Revenue'];
};

// ─────────────────────────────────────────────
// SKELETON COMPONENTS
// ─────────────────────────────────────────────
const SkeletonStatCards = () => (
  <div className="revenue-stats-container">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="skeleton-stat-card">
        <div className="skeleton skeleton-icon" />
        <div className="skeleton skeleton-title" />
        <div className="skeleton skeleton-amount" />
      </div>
    ))}
  </div>
);

const SkeletonChart = () => {
  const barHeights = [55, 75, 60, 85, 70, 90, 50, 80, 65, 88, 72, 95];
  return (
    <div className="skeleton-chart-container em-section">
      <div className="skeleton-chart-header">
        <div className="skeleton-chart-info">
          <div className="skeleton skeleton-chart-label" />
          <div className="skeleton skeleton-chart-amount" />
          <div className="skeleton skeleton-chart-change" />
        </div>
        <div className="skeleton-tabs">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton skeleton-tab" />
          ))}
        </div>
      </div>
      <div className="skeleton-chart-body">
        {barHeights.map((h, i) => (
          <div key={i} className="skeleton skeleton-bar" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════
const EarningsPage = () => {
  const { baseUrl ,authToken } = useContext(AppContext);

  // ── States ────────────────────────────────────────────────────────────────
  const [earningsSummary, setEarningsSummary] = useState({
    totalRevenue:       0,
    rentalPayout:       0,
    mechanicTopup:      0,
    platformCommission: 0,
  });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [graphLoading,   setGraphLoading]   = useState(true);

  // ✅ graphData state — GRAPH_DATA constant removed
  const [graphData, setGraphData] = useState({
    daily:   [],
    weekly:  [],
    monthly: [],
  });

  const [activeTab, setActiveTab] = useState('monthly');

  // ── API Call ──────────────────────────────────────────────────────────────
  const fetchEarningsSummary = () => {
    axios.get(`${baseUrl}/superadmin/earning/getEarningsSummary`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((res) => {
        if (res.data.success) {
          // Stat cards data
          setEarningsSummary({
            totalRevenue:       res.data.totalRevenue,
            rentalPayout:       res.data.rentalPayout,
            mechanicTopup:      res.data.mechanicTopup,
            platformCommission: res.data.platformCommission,
          });

          // ✅ Graph data backend se set karo
          if (res.data.graphData) {
            setGraphData({
              daily:   res.data.graphData.daily   || [],
              weekly:  res.data.graphData.weekly  || [],
              monthly: res.data.graphData.monthly || [],
            });
          }
        }
        setSummaryLoading(false);
        setGraphLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setSummaryLoading(false);
        setGraphLoading(false);
      });
  };

  useEffect(() => {
    fetchEarningsSummary();
  }, []);

  // ── Stat Cards config ─────────────────────────────────────────────────────
  const statCards = [
    {
      id:     1,
      title:  'Total Revenue',
      amount: `Rs ${Number(earningsSummary.totalRevenue).toLocaleString()}`,
      icon:   FiCreditCard,
    },
    {
      id:     2,
      title:  'Rental Payout',
      amount: `Rs ${Number(earningsSummary.rentalPayout).toLocaleString()}`,
      icon:   FiTrendingUp,
    },
    {
      id:     3,
      title:  'Mechanic Topup',
      amount: `Rs ${Number(earningsSummary.mechanicTopup).toLocaleString()}`,
      icon:   FiDollarSign,
    },
    {
      id:     4,
      title:  'Platform Commission',
      amount: `Rs ${Number(earningsSummary.platformCommission).toLocaleString()}`,
      icon:   FiPercent,
    },
  ];

  // ── Active tab ka data ────────────────────────────────────────────────────
  // ✅ State se lena hai — koi const override nahi
  const activeGraphData = graphData[activeTab] || [];

  // ── Graph meta dynamically calculate ─────────────────────────────────────
  const graphMeta = useMemo(() => {
    const total = activeGraphData.reduce((sum, d) => sum + (d.revenue || 0), 0);
    const formatted = total >= 1000000
      ? `Rs ${(total / 1000000).toFixed(2)}M`
      : `Rs ${total.toLocaleString()}`;
    return { total, formatted };
  }, [activeGraphData]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <MasterLayout>
      <div className="em-shell">
        <div className="em-main">

          {/* ── PAGE HEADER ── */}
          <div className="em-header">
            <h1>Earnings Management</h1>
            <p>Track revenue, mechanic topups and platform commission</p>
          </div>

          {/* ── STAT CARDS ── */}
          {summaryLoading ? (
            <SkeletonStatCards />
          ) : (
            <div className="revenue-stats-container">
              {statCards.map((stat) => (
                <div key={stat.id} className="revenue-stat-card">
                  <div className="revenue-stat-header">
                    <div className="revenue-stat-icon">
                      <stat.icon size={26} />
                    </div>
                    <span className="revenue-stat-title">{stat.title}</span>
                  </div>
                  <div className="revenue-stat-body">
                    <h3 className="revenue-stat-amount">{stat.amount}</h3>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── REVENUE CHART ── */}
          {graphLoading ? (
            <SkeletonChart />
          ) : (
            <div className="revenue-chart-container em-section">
              <div className="revenue-chart-header">
                <div className="revenue-info">
                  <p className="revenue-label">Revenue Over Time</p>
                  {/* ✅ Dynamic total from real data */}
                  <h2 className="revenue-amount">{graphMeta.formatted}</h2>
                </div>

                <div className="revenue-tabs">
                  {['daily', 'weekly', 'monthly'].map(tab => (
                    <button
                      key={tab}
                      className={`revenue-tab ${activeTab === tab ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="revenue-chart-wrapper">
                <ResponsiveContainer width="100%" height={280}>
                  {/* ✅ activeGraphData — state se, override nahi */}
                  <AreaChart
                    data={activeGraphData}
                    margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#FDB714" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#FDB714" stopOpacity={0}   />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                    <XAxis
                      dataKey="label"
                      stroke="#999999"
                      tick={{ fontSize: 12, fontWeight: 500, fontFamily: 'Poppins' }}
                    />
                    <YAxis
                      stroke="#999999"
                      tick={{ fontSize: 11, fontFamily: 'Poppins' }}
                      tickFormatter={formatAxisTick}
                      width={80}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#000000',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '8px 14px',
                        fontFamily: 'Poppins',
                      }}
                      labelStyle={{ color: '#FDB714', marginBottom: 4 }}
                      formatter={formatTooltipValue}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#FDB714"
                      strokeWidth={3}
                      fill="url(#revenueGradient)"
                      dot={{ fill: '#FDB714', r: 4, strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: '#FDB714', stroke: '#000', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

        </div>
      </div>
    </MasterLayout>
  );
};

export default EarningsPage;